import { useEffect, useState } from 'react';

import useSecuredApiV2 from './use-secured-api-v2';
import ProblemDetailsDto from '../dto/error/problem-details-dto';
import { type ResponseDto } from '../dto/ResponseDto';
import { type ScheduledMealResponseDto } from '../dto/scheduled-meal/scheduled-meal-response-dto';
import { SCHEDULED_MEALS_ENDPOINT_URL } from '../utils/endpoints';

export interface ScheduledMealsListParams {
  day?: string;
  siteId?: number;
  pageNumber: number;
  pageSize: number;
}

interface ScheduledMealsListState {
  data: ScheduledMealResponseDto[];
  totalRecords: number;
  isLoading: boolean;
  errorMessage?: string;
}

export interface ScheduledMealsListResult extends ScheduledMealsListState {
  refetch: () => void;
}

const toErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof ProblemDetailsDto) return error.detail;
  if (error instanceof Error) return error.message;
  return undefined;
};

/**
 * Loads a page of scheduled meals for a given date and site from the Spring Boot ScheduledMealController.
 * It reloads whenever "params" change or "refetch" is called. A failed load clears the list and sets "errorMessage".
 */
const useScheduledMealsList = (params: ScheduledMealsListParams): ScheduledMealsListResult => {
  const api = useSecuredApiV2();
  const [state, setState] = useState<ScheduledMealsListState>({
    data: [],
    totalRecords: 0,
    isLoading: false,
  });
  const [reloadCount, setReloadCount] = useState(0);
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    // Only fetch when both mandatory filters (day and siteId) are present
    if (!params.day || !params.siteId) {
      setState({
        data: [],
        totalRecords: 0,
        isLoading: false,
      });
      return;
    }

    setState((previous) => ({ ...previous, isLoading: true, errorMessage: undefined }));

    const subscription = api
      .get(
        SCHEDULED_MEALS_ENDPOINT_URL,
        {
          day: params.day,
          siteId: params.siteId,
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
        },
        {}
      )
      .subscribe({
        next: (response: ResponseDto<ScheduledMealResponseDto[]>) => {
          setState({
            data: response.data,
            totalRecords: response.pagination?.totalRecords ?? 0,
            isLoading: false,
          });
        },
        error: (error: unknown) => {
          setState({
            data: [],
            totalRecords: 0,
            isLoading: false,
            errorMessage: toErrorMessage(error) ?? 'Failed to load scheduled meals',
          });
        },
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [paramsKey, reloadCount]);

  const refetch = (): void => {
    setReloadCount((count) => count + 1);
  };

  return { ...state, refetch };
};

export default useScheduledMealsList;
